import React, { useMemo } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { UniversalCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import {
  ApiServiceCodeOptions,
  ProductCodeOptions,
  TermsOfTradeOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/UniversalCourier/constants';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}
const universalValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  apiServiceCode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Service code value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one API Service Code is required')
    .required('API Service Code is required')
    .default([]),

  productCode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Product code value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Product Code is required')
    .required('Product Code is required')
    .default([]),

  termsOfTrade: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Terms of Trade value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Terms of Trade is required')
    .required('Terms of Trade is required')
    .default([]),
});

const Universal = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);

  const { values, setFieldValue, errors, handleSubmit } = useFormik<UniversalCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: universalValidationSchema,
    initialValues: {
      ...carrierSetting.universal,
    },
    onSubmit(values) {
      if (userDataById?._id) {
        if (carrierSetting.universal._id) {
          dispatch(updateCarrierSettings({ ...carrierSetting.universal, ...values }));
        } else {
          dispatch(addCarrierSettings({ ...carrierSetting.universal, ...values }));
        }
      } else {
        dispatch(setCarrierSettings({ values, carrierType: 'universal' }));
      }
      onClose();
    },
  });

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  console.info('values', values);

  return (
    <div>
      <>
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Universal Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label={`Universal Account`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="accountNo"
                    value={values?.accountNo.map(item => item.value)}
                    options={accountOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="apiServiceCode"
                    label={`API Service Code`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="apiServiceCode"
                    value={values?.apiServiceCode.map(item => item.value)}
                    options={ApiServiceCodeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('apiServiceCode', selectedObj);
                    }}
                    error={errors.apiServiceCode as string}
                  />
                </Grid>
              </Grid>

              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="productCode"
                    label={`Product Code`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="productCode"
                    value={values?.productCode.map(item => item.value)}
                    options={ProductCodeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('productCode', selectedObj);
                    }}
                    error={errors.productCode as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="termsOfTrade"
                    label={`Terms of Trade`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="termsOfTrade"
                    value={values?.termsOfTrade.map(item => item.value)}
                    options={TermsOfTradeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('termsOfTrade', selectedObj);
                    }}
                    error={errors.termsOfTrade as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}></Grid>
              </Grid>
              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="submit"
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        type="button"
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    </div>
  );
};

export default Universal;
